import React, { useRef, useEffect, useCallback } from "react";
import { Share2, Download, Link as LinkIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";

const truncate = (s, n) => (s && s.length > n ? s.slice(0, n - 1) + "…" : s || "");
const weekLabel = (w) => { const m = /^(\d{4})-W(\d{2})$/.exec(w || ""); return m ? `W${parseInt(m[2], 10)}` : w; };

// Controlled brag card for the reigning dynasty (most Clan-of-the-Week trophies). Parent owns `open`.
export default function DynastyCard({ clan, open, onOpenChange }) {
  const canvasRef = useRef(null);
  const shareUrl = `${window.location.origin}/app/multiplayer`;
  const shortUrl = shareUrl.replace(/^https?:\/\//, "");
  const c = clan || {};
  const weeks = c.weeks || [];

  const draw = useCallback(() => {
    const cv = canvasRef.current; if (!cv) return;
    const ctx = cv.getContext("2d");
    const W = 1200, H = 630;
    const accent = c.banner || "#b45309";
    const grad = ctx.createLinearGradient(0, 0, W, H);
    grad.addColorStop(0, "#1a1206"); grad.addColorStop(1, accent);
    ctx.fillStyle = grad; ctx.fillRect(0, 0, W, H);
    ctx.fillStyle = "rgba(0,0,0,0.42)"; ctx.fillRect(0, 0, W, H);
    const rad = ctx.createRadialGradient(300, 300, 30, 300, 300, 560);
    rad.addColorStop(0, "rgba(255,209,102,0.30)"); rad.addColorStop(1, "rgba(0,0,0,0)");
    ctx.fillStyle = rad; ctx.fillRect(0, 0, W, H);
    ctx.textBaseline = "top";
    ctx.fillStyle = "rgba(255,209,102,0.95)"; ctx.font = "bold 32px Inter, system-ui, sans-serif";
    ctx.fillText("🏆 GAMEVIEW STUDIO · DYNASTY", 60, 52);
    ctx.textBaseline = "middle"; ctx.font = "170px serif";
    ctx.fillText(c.emoji || "🛡️", 80, 340);
    ctx.textBaseline = "alphabetic"; ctx.fillStyle = "#fff"; ctx.font = "bold 66px Inter, system-ui, sans-serif";
    ctx.fillText(truncate(c.name || "The Dynasty", 20), 320, 250);
    if (c.tag) { ctx.fillStyle = "rgba(255,255,255,0.6)"; ctx.font = "bold 30px monospace"; ctx.fillText(`[${c.tag}]`, 320, 292); }
    ctx.fillStyle = "#ffd166"; ctx.font = "bold 86px Inter, system-ui, sans-serif";
    ctx.fillText(`${c.trophies || 0}× 🏆`, 320, 395);
    ctx.fillStyle = "rgba(255,255,255,0.9)"; ctx.font = "bold 32px Inter, system-ui, sans-serif";
    ctx.fillText("CLAN OF THE WEEK", 320, 452);
    ctx.fillStyle = "rgba(255,255,255,0.78)"; ctx.textBaseline = "top"; ctx.font = "26px Inter, system-ui, sans-serif";
    const wk = weeks.slice(0, 8).map(weekLabel).join("  ·  ");
    ctx.fillText(truncate(`Reigned: ${wk || "—"}`, 60), 320, 498);
    ctx.fillStyle = "rgba(255,255,255,0.72)"; ctx.font = "26px Inter, system-ui, sans-serif";
    ctx.fillText(`▶ Dethrone them — ${truncate(shortUrl, 42)}`, 60, 578);
  }, [c.banner, c.emoji, c.name, c.tag, c.trophies, weeks, shortUrl]);

  useEffect(() => { if (open) { const t = setTimeout(draw, 60); return () => clearTimeout(t); } }, [open, draw]);

  const toBlob = () => new Promise((res) => canvasRef.current.toBlob(res, "image/png"));
  const download = async () => {
    const blob = await toBlob(); if (!blob) return;
    const url = URL.createObjectURL(blob);
    const el = document.createElement("a");
    el.href = url; el.download = `dynasty-${(c.name || "clan").replace(/[^a-z0-9]+/gi, "-").toLowerCase()}.png`;
    el.click(); URL.revokeObjectURL(url);
    toast.success("Dynasty card downloaded!");
  };
  const copyLink = async () => {
    try { await navigator.clipboard.writeText(shareUrl); toast.success("Link copied — rally the crew!"); }
    catch { toast.error("Couldn't copy the link."); }
  };
  const nativeShare = async () => {
    const text = `${c.name} has reigned as Clan of the Week ${c.trophies}× on GameView Studio! 🏆`;
    try {
      const blob = await toBlob();
      const file = blob ? new File([blob], "dynasty.png", { type: "image/png" }) : null;
      if (file && navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title: c.name, text, url: shareUrl });
      } else {
        await navigator.share({ title: c.name, text, url: shareUrl });
      }
    } catch { /* cancelled / unsupported */ }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg" data-testid="dynasty-card-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><Share2 className="w-5 h-5 text-amber-400" /> Share the dynasty</DialogTitle>
          <DialogDescription>Download or share a card celebrating your clan's Clan-of-the-Week reign.</DialogDescription>
        </DialogHeader>
        <canvas ref={canvasRef} width={1200} height={630} className="w-full rounded-xl border border-[hsl(var(--border))]" data-testid="dynasty-card-canvas" />
        <div className="flex flex-wrap gap-2">
          <Button onClick={download} className="rounded-full gap-1.5 flex-1 bg-gradient-to-r from-amber-500 to-orange-500" data-testid="dynasty-card-download"><Download className="w-4 h-4" /> Download image</Button>
          <Button variant="outline" onClick={copyLink} className="rounded-full gap-1.5 flex-1" data-testid="dynasty-card-copy"><LinkIcon className="w-4 h-4" /> Copy link</Button>
          {typeof navigator !== "undefined" && navigator.share && (
            <Button variant="outline" onClick={nativeShare} className="rounded-full gap-1.5 flex-1" data-testid="dynasty-card-native"><Share2 className="w-4 h-4" /> Share…</Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
